import { CustomColorThemesProvider } from "./contexts/CustomColorThemesContext";
import { DeepLinkContextProvider } from "./contexts/DeepLinkContext";
import { StaticSiteDataProvider } from "./contexts/StaticSiteDataContext";
import { TrackContextProvider } from "./contexts/TrackContext";
import { VisibleGuestListProvider } from "./contexts/VisibleGuestListContext";
import API from "./core/api";
import { deepCamelCaseKeys } from "./core/api/drf";
import { SentryBoundary } from "./core/components/SentryBoundary";
import { trackPageLoad } from "./core/instrumentation";
import { createRoutes, unauthenticatedRoutes } from "./core/routing";
import { RouterSetup } from "./core/routing/RouterSetup";
import configureStore from "./core/store";
import RefManager from "./ref-monitor/RefManager";
import RefMonitorProvider from "./ref-monitor/components/RefMonitorProvider";
import { loadSiteData } from "./website/actions";
import LoginWrapper from "./website/components/LoginWrapper";
import Website from "./website/components/Website";
import OnlineInvitationSite from "./website/components/online-invitation/OnlineInvitationSite";
import { selectSite, filterPage } from "./website/selectors";
import config from "config";
import React from "react";
import { render } from "react-dom";
import ReactGA from "react-ga";
import { Provider } from "react-redux";
import smoothScroll from "smoothscroll-polyfill";
import { v4 as uuidV4 } from "uuid";

smoothScroll.polyfill();

ReactGA.initialize(config.get("guestGATrackingID"));

const siteData = deepCamelCaseKeys(window.__DATA__, ["themeDataOverrides"]);

const {
  accommodations,
  attendants,
  comments,
  customColorThemes,
  events,
  imageUploads,
  pages,
  photos,
  registries,
  videos,
  ...rest
} = siteData;

const data = {
  accommodations,
  attendants,
  comments,
  events,
  "image-uploads": imageUploads,
  pages,
  photos,
  registries,
  sites: [rest],
  videos,
};

const baseUrl = window.__BASE_URL__;
const guestAuthenticated = window.__GUEST_AUTHENTICATED__ === "True";

let routes;

if (guestAuthenticated) {
  routes = createRoutes(pages.filter((page) => filterPage(page, data)));
} else {
  routes = unauthenticatedRoutes;
}

const deepLinkContactId = window.__ADDRESSBOOK_CONTACT_ID__;

API.setDomain(siteData.domain);

const store = configureStore();

store.dispatch(loadSiteData(data));

const refManager = new RefManager();

const state = store.getState();

const site = selectSite(state.resources.sites);

let SiteComponent;

if (site.isWedding) {
  SiteComponent = Website;
} else {
  SiteComponent = OnlineInvitationSite;
}

store.dispatch({
  payload: window.__THEME_DATA__,
  type: "FETCH_THEME_FULFILLED",
});

let guestId = localStorage.getItem("mintedWebsitesGuestId");

if (!guestId) {
  guestId = uuidV4();

  localStorage.setItem("mintedWebsitesGuestId", guestId);
}

trackPageLoad(
  site.isDemoSite ? "site_demo" : "site_guest_experience",
  site,
  guestId,
);

render(
  <SentryBoundary>
    <Provider store={store}>
      <RouterSetup basename={baseUrl} routes={routes} syncWithRedux={true}>
        <RefMonitorProvider refManager={refManager}>
          <StaticSiteDataProvider site={site}>
            <VisibleGuestListProvider>
              <TrackContextProvider site={site}>
                <CustomColorThemesProvider
                  initialCustomColorThemes={customColorThemes || []}
                >
                  <LoginWrapper>
                    <DeepLinkContextProvider
                      addressbookContactId={deepLinkContactId}
                    >
                      <SiteComponent />
                    </DeepLinkContextProvider>
                  </LoginWrapper>
                </CustomColorThemesProvider>
              </TrackContextProvider>
            </VisibleGuestListProvider>
          </StaticSiteDataProvider>
        </RefMonitorProvider>
      </RouterSetup>
    </Provider>
  </SentryBoundary>,
  document.getElementById("root"),
);
